/**
 * Seção: EnderecoCompleto
 * Formulário completo de endereço (CEP, Rua, Número, etc)
 * Integrado com via-cep para auto-preenchimento
 */

import React, { useState, useEffect } from 'react';
import { FormFieldGroup } from '../components';
import { useFormValidation } from '../hooks';

export interface EnderecoData {
  cep: string;
  address: string;
  addressNumber: string;
  complement?: string;
  neighborhood: string;
  city?: string;
  state?: string;
}

interface EnderecoCompletoProps {
  data: EnderecoData;
  onChange: (newData: Partial<EnderecoData>) => void;
}

export const EnderecoCompleto: React.FC<EnderecoCompletoProps> = ({ data, onChange }) => {
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoadingCep, setIsLoadingCep] = useState(false);
  const { validateCEP, validateText } = useFormValidation();

  const handleCEPChange = async (value: string) => {
    const cleaned = value.replace(/\D/g, '');
    const formatted = cleaned.replace(/(\d{5})(\d{3})/, '$1-$2');
    onChange({ cep: formatted });

    // Validar CEP
    const validation = validateCEP(formatted);
    if (!validation.valid) {
      setErrors((prev) => ({ ...prev, cep: validation.error || '' }));
      return;
    }

    // Se CEP é válido e tem 8 dígitos, buscar no via-cep
    if (cleaned.length === 8) {
      setIsLoadingCep(true);
      try {
        const response = await fetch(`https://viacep.com.br/ws/${cleaned}/json/`);
        const data = await response.json();

        if (!data.erro) {
          onChange({
            address: data.logradouro || '',
            neighborhood: data.bairro || '',
            city: data.localidade || '',
            state: data.uf || '',
          });
          setErrors((prev) => {
            const newErrors = { ...prev };
            delete newErrors.cep;
            return newErrors;
          });
        } else {
          setErrors((prev) => ({ ...prev, cep: 'CEP não encontrado' }));
        }
      } catch (error) {
        console.error('Erro ao buscar CEP:', error);
        setErrors((prev) => ({ ...prev, cep: 'Erro ao buscar CEP' }));
      } finally {
        setIsLoadingCep(false);
      }
    }
  };

  const handleAddressChange = (value: string) => {
    onChange({ address: value });
    const validation = validateText(value, 3);
    if (!validation.valid) {
      setErrors((prev) => ({ ...prev, address: validation.error || '' }));
    } else {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.address;
        return newErrors;
      });
    }
  };

  const handleNumberChange = (value: string) => {
    onChange({ addressNumber: value });
    const validation = validateText(value, 1);
    if (!validation.valid) {
      setErrors((prev) => ({ ...prev, addressNumber: validation.error || '' }));
    } else {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.addressNumber;
        return newErrors;
      });
    }
  };

  const handleNeighborhoodChange = (value: string) => {
    onChange({ neighborhood: value });
    const validation = validateText(value, 2);
    if (!validation.valid) {
      setErrors((prev) => ({ ...prev, neighborhood: validation.error || '' }));
    } else {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.neighborhood;
        return newErrors;
      });
    }
  };

  return (
    <div className="endereco-completo-section">
      <FormFieldGroup label="CEP" required error={errors.cep}>
        <input
          type="text"
          placeholder="12345-678"
          maxLength={9}
          value={data.cep}
          onChange={(e) => handleCEPChange(e.target.value)}
          disabled={isLoadingCep}
          className="form-input"
        />
        {isLoadingCep && <p className="text-sm text-muted-foreground mt-1">Buscando endereço...</p>}
      </FormFieldGroup>

      <FormFieldGroup label="Rua/Avenida" required error={errors.address}>
        <input
          type="text"
          placeholder="Rua Principal"
          value={data.address}
          onChange={(e) => handleAddressChange(e.target.value)}
          className="form-input"
        />
      </FormFieldGroup>

      <div className="input-row">
        <FormFieldGroup label="Número" required error={errors.addressNumber}>
          <input
            type="text"
            placeholder="123"
            value={data.addressNumber}
            onChange={(e) => handleNumberChange(e.target.value)}
            className="form-input"
          />
        </FormFieldGroup>

        <FormFieldGroup label="Complemento" error={errors.complement}>
          <input
            type="text"
            placeholder="Apto 42"
            value={data.complement || ''}
            onChange={(e) => onChange({ complement: e.target.value })}
            className="form-input"
          />
        </FormFieldGroup>
      </div>

      <FormFieldGroup label="Bairro" required error={errors.neighborhood}>
        <input
          type="text"
          placeholder="Centro"
          value={data.neighborhood}
          onChange={(e) => handleNeighborhoodChange(e.target.value)}
          className="form-input"
        />
      </FormFieldGroup>

      {data.city && data.state && (
        <FormFieldGroup label="Cidade/Estado">
          <input
            type="text"
            placeholder="Cidade/UF"
            value={`${data.city}, ${data.state}`}
            readOnly
            className="form-input bg-muted text-muted-foreground"
          />
        </FormFieldGroup>
      )}
    </div>
  );
};

export default EnderecoCompleto;
